import type {
  ApiResponse,
  UserProfileResponse,
  UserBalanceResponse,
  UserMinersResponse,
  TransactionsResponse,
  PublicConfigResponse,
  ClaimResponse,
  TaskDto,
  TaskVerifyResponse,
  CreateStarsInvoiceResponse,
  StarsOrderStatusResponse,
  ReferralsResponse,
} from '@nova-mine/shared';

const API_BASE = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8787';

let authToken: string | null = localStorage.getItem('nova_mine_token');

export function setAuthToken(token: string) {
  authToken = token;
  localStorage.setItem('nova_mine_token', token);
}

export function getAuthToken(): string | null {
  return authToken;
}

export function clearAuthToken() {
  authToken = null;
  localStorage.removeItem('nova_mine_token');
}

async function request<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
  const headers = new Headers(options.headers || {});
  headers.set('Content-Type', 'application/json');

  if (authToken) {
    headers.set('Authorization', `Bearer ${authToken}`);
  }

  const response = await fetch(`${API_BASE}${endpoint}`, {
    ...options,
    headers,
  });

  const json: ApiResponse<T> = await response.json().catch(() => ({
    success: false,
    error: `Server communication failed (${response.status} ${response.statusText || 'Error'})`,
  }));

  if (!response.ok || !json.success) {
    throw new Error(json.error || `Request failed with status ${response.status}`);
  }

  return json.data as T;
}

export const api = {
  // Authenticate with Telegram InitData
  async loginTelegram(initData: string, referralCode?: string) {
    const res = await request<{ token: string; user: any }>('/api/auth/telegram', {
      method: 'POST',
      body: JSON.stringify({ initData, referralCode }),
    });
    setAuthToken(res.token);
    return res;
  },

  // Get current user profile and live mining status
  async getProfile(): Promise<UserProfileResponse> {
    return request<UserProfileResponse>('/api/me');
  },

  // Get authoritative balance
  async getBalance(): Promise<UserBalanceResponse> {
    return request<UserBalanceResponse>('/api/me/balance');
  },

  // Get user miners
  async getMiners(): Promise<UserMinersResponse> {
    return request<UserMinersResponse>('/api/me/miners');
  },

  // Partial or full claim coins
  async claimCoins(
    miner_type: 'all' | 'main' | 'auto' | 'stars' = 'all',
    miner_id?: string,
    idempotencyKey?: string
  ): Promise<ClaimResponse> {
    const headers: Record<string, string> = {};
    if (idempotencyKey) {
      headers['Idempotency-Key'] = idempotencyKey;
    }
    return request<ClaimResponse>('/api/miners/claim', {
      method: 'POST',
      headers,
      body: JSON.stringify({ miner_type, miner_id }),
    });
  },

  // Get user transaction ledger history
  async getTransactions(): Promise<TransactionsResponse> {
    return request<TransactionsResponse>('/api/me/transactions');
  },

  // Get user referral statistics and verified referrals list
  async getReferrals(): Promise<ReferralsResponse> {
    return request<ReferralsResponse>('/api/me/referrals');
  },

  // Get public network configuration
  async getPublicConfig(): Promise<PublicConfigResponse> {
    return request<PublicConfigResponse>('/api/config/public');
  },

  // Get tasks list
  async getTasks(): Promise<{
    tasks: TaskDto[];
    completed_website_tasks: number;
    required_for_auto_miner: number;
    auto_miner_unlocked: boolean;
  }> {
    return request('/api/tasks');
  },

  // Start a website-view task quest with a short-lived server attempt
  async startTask(taskId: string): Promise<{
    attempt_id: string;
    task_id: string;
    url: string;
    min_dwell_seconds: number;
    expires_at: number;
  }> {
    return request(`/api/tasks/${taskId}/start`, {
      method: 'POST',
    });
  },

  // Verify task completion via attempt_id
  async verifyTask(
    attemptOrTaskId: string | number,
    placementType?: string
  ): Promise<TaskVerifyResponse> {
    const isAttempt = String(attemptOrTaskId).startsWith('att_');
    const body = isAttempt
      ? { attempt_id: String(attemptOrTaskId), placement_type: placementType }
      : { task_id: attemptOrTaskId, placement_type: placementType };

    return request<TaskVerifyResponse>('/api/tasks/verify', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  },

  // Start an authorized AdsGram placement attempt
  async startAdAttempt(
    placementType: 'rewarded' | 'interstitial' = 'rewarded',
    sequenceStep: number = 1
  ): Promise<{
    attempt_id: string;
    placement_type: string;
    block_id: string;
    sequence_step: number;
    expires_at: number;
  }> {
    return request('/api/ads/start', {
      method: 'POST',
      body: JSON.stringify({ placement_type: placementType, sequence_step: sequenceStep }),
    });
  },

  // Complete an authorized AdsGram placement attempt
  async completeAdAttempt(attemptId: string): Promise<{
    success: boolean;
    attempt_id: string;
    sequence_completed: boolean;
    reward_coins: number;
    new_balance: number;
    main_miner_boosted: boolean;
    message?: string;
  }> {
    return request('/api/ads/complete', {
      method: 'POST',
      body: JSON.stringify({ attempt_id: attemptId }),
    });
  },

  // Create Stars Miner Invoice
  async createStarsInvoice(unitsCount: number): Promise<CreateStarsInvoiceResponse> {
    return request<CreateStarsInvoiceResponse>('/api/stars/create-invoice', {
      method: 'POST',
      body: JSON.stringify({ units_count: unitsCount }),
    });
  },

  // Authoritative check of Stars order status from backend
  async checkStarsOrderStatus(orderId: string): Promise<StarsOrderStatusResponse> {
    return request<StarsOrderStatusResponse>(`/api/stars/orders/${orderId}/status`);
  },

  // Cancel pending Stars order
  async cancelStarsOrder(orderId: string): Promise<{ success: boolean; status: string }> {
    return request(`/api/stars/orders/${orderId}/cancel`, {
      method: 'POST',
    });
  },
};
